import { WidgetType as WidgetTypeConstant } from '@/modules/ta/widget/widget.constants';

export class WidgetType {
  /**
   * @type {boolean}
   */
  can_group_by;

  /**
   * @type {boolean}
   */
  can_drilldown;

  /**
   * @type {boolean}
   */
  requires_group_by;

  /**
   * @type {string}
   */
  id;

  /**
   * @type {string}
   */
  name;

  /**
   * @type {string}
   */
  image;

  /**
   * @type {string}
   */
  largeImage;

  /**
   * @type {string[]}
   */
  type_groupings;

  constructor(model = {}) {
    this.can_group_by = model.can_group_by;
    this.can_drilldown = model.can_drilldown;
    this.requires_group_by = model.requires_group_by;
    this.id = model.id;
    this.name = model.name;
    this.image = model.image;
    this.largeImage = model.largeImage;
    this.type_groupings = model.type_groupings;
  }

  /**
   * @returns {boolean}
   */
  supportsCumulativeData() {
    switch (this.type) {
      case WidgetTypeConstant.TYPE_BARCHART:
      case WidgetTypeConstant.TYPE_LINECHART:
      case WidgetTypeConstant.TYPE_COMBINATIONCHART:
        return true;
      default:
        return false;
    }
  }

  /**
   * @returns {boolean}
   */
  isDataSourcedWidgetType() {
    // eslint-disable-next-line tap/no-raw-text-js
    return this.type_groupings.includes('datasourced');
  }

  /**
   * @returns {boolean}
   */
  isAdminWidgetType() {
    // eslint-disable-next-line tap/no-raw-text-js
    return this.type_groupings.includes('admin');
  }

  /**
   * @returns {boolean}
   */
  isDataWidgetType() {
    switch (this.id) {
      case WidgetTypeConstant.TYPE_ACCOUNTMANAGER:
      case WidgetTypeConstant.TYPE_MEDIA:
      case WidgetTypeConstant.TYPE_GOAL:
      case WidgetTypeConstant.TYPE_EXECUTIVESUMMARY:
        return false;
      default:
        return true;
    }
  }

  /**
   * @returns {boolean}
   */
  isDisplayWidgetType() {
    return this.id === WidgetTypeConstant.TYPE_MEDIA;
  }

  /**
   * @returns {boolean}
   */
  isChatGptWidgetType() {
    return this.id === WidgetTypeConstant.TYPE_CHATGPT;
  }

  /**
   * @returns {boolean}
   */
  isExecSummaryWidgetType() {
    return this.id === WidgetTypeConstant.TYPE_EXECUTIVESUMMARY;
  }

  /**
   * @returns {boolean}
   */
  isDrilldownWidgetType() {
    switch (this.id) {
      case WidgetTypeConstant.TYPE_BARCHART:
      case WidgetTypeConstant.TYPE_LINECHART:
      case WidgetTypeConstant.TYPE_COMBINATIONCHART:
      case WidgetTypeConstant.TYPE_PIECHART:
        return true;
      default:
        return false;
    }
  }

  /**
   * @returns {boolean}
   */
  isSeriesWidgetType() {
    // eslint-disable-next-line tap/no-raw-text-js
    return this.type_groupings.includes('series');
  }

  getWidgetType(widgetTypeId) {
    switch (widgetTypeId) {
      case WidgetTypeConstant.TYPE_BIGNUMBER:
        return WidgetType.getBigNumberType();
      case WidgetTypeConstant.TYPE_DATAGRID:
        return WidgetType.getDataGridType();
      case WidgetTypeConstant.TYPE_BARCHART:
        return WidgetType.getBarChartType();
      case WidgetTypeConstant.TYPE_LINECHART:
        return WidgetType.getLineChartType();
      case WidgetTypeConstant.TYPE_COMBINATIONCHART:
        return WidgetType.getCombinationChartType();
      case WidgetTypeConstant.TYPE_BUBBLECHART:
        return WidgetType.getBubbleChartType();
      case WidgetTypeConstant.TYPE_PIECHART:
        return WidgetType.getPieChartType();
      case WidgetTypeConstant.TYPE_FUNNELCHART:
        return WidgetType.getFunnelChartType();
      case WidgetTypeConstant.TYPE_GEOCHART:
        return WidgetType.getGeoChartType();
      case WidgetTypeConstant.TYPE_GAUGECHART:
        return WidgetType.getGaugeChartType();
      case WidgetTypeConstant.TYPE_MEDIA:
        return WidgetType.getMediaType();
      case WidgetTypeConstant.TYPE_ACCOUNTMANAGER:
        return WidgetType.getAccountManagerType();
      case WidgetTypeConstant.TYPE_GOAL:
        return WidgetType.getGoalType();
      case WidgetTypeConstant.TYPE_GOALCHART:
        return WidgetType.getGoalChartType();
      case WidgetTypeConstant.TYPE_CHATGPT:
        return WidgetType.getChatGPTType();
      case WidgetTypeConstant.TYPE_SANKEYCHART:
        return WidgetType.getSankeyChartType();
      case WidgetTypeConstant.TYPE_PIVOTTABLE:
        return WidgetType.getPivotTableType();
      default:
        Logger.log(i18n.$t('Unsupported widget type'), Logger.LEVEL_ERROR);
        break;
    }
  }

  /**
   * @returns {WidgetType}
   */
  static getBigNumberType() {
    return new WidgetType({
      can_group_by: false,
      can_drilldown: false,
      requires_group_by: false,
      id: WidgetTypeConstant.TYPE_BIGNUMBER,
      name: i18n.$t('Big Number'),
      /* eslint-disable tap/no-raw-text-js */
      image: 'bignumber-classic',
      largeImage: 'bignumber-classic',
      type_groupings: ['datasourced'],
      /* eslint-enable tap/no-raw-text-js */
    });
  }

  /**
   * @returns {WidgetType}
   */
  static getDataGridType() {
    return new WidgetType({
      can_group_by: true,
      can_drilldown: false,
      requires_group_by: false,
      id: WidgetTypeConstant.TYPE_DATAGRID,
      name: i18n.$t('Data Grid'),
      /* eslint-disable tap/no-raw-text-js */
      image: 'datagrid-default',
      largeImage: 'datagrid-default',
      type_groupings: ['datasourced'],
      /* eslint-enable tap/no-raw-text-js */
    });
  }

  /**
   * @returns {WidgetType}
   */
  static getBarChartType() {
    return new WidgetType({
      can_group_by: true,
      can_drilldown: true,
      requires_group_by: true,
      id: WidgetTypeConstant.TYPE_BARCHART,
      name: i18n.$t('Bar Chart'),
      /* eslint-disable tap/no-raw-text-js */
      image: 'barchart-clustered',
      largeImage: 'barchart-clustered',
      type_groupings: ['datasourced', 'chart', 'series'],
      /* eslint-enable tap/no-raw-text-js */
    });
  }

  /**
   * @returns {WidgetType}
   */
  static getLineChartType() {
    return new WidgetType({
      can_group_by: true,
      can_drilldown: true,
      requires_group_by: true,
      id: WidgetTypeConstant.TYPE_LINECHART,
      name: i18n.$t('Line Chart'),
      /* eslint-disable tap/no-raw-text-js */
      image: 'linechart-line',
      largeImage: 'linechart-line',
      type_groupings: ['datasourced', 'chart', 'series'],
      /* eslint-enable tap/no-raw-text-js */
    });
  }

  /**
   * @returns {WidgetType}
   */
  static getCombinationChartType() {
    return new WidgetType({
      can_group_by: true,
      can_drilldown: true,
      requires_group_by: true,
      id: WidgetTypeConstant.TYPE_COMBINATIONCHART,
      name: i18n.$t('Combo Chart'),
      /* eslint-disable tap/no-raw-text-js */
      image: 'combochart-clustered',
      largeImage: 'combochart-clustered',
      type_groupings: ['datasourced', 'chart', 'series'],
      /* eslint-enable tap/no-raw-text-js */
    });
  }

  /**
   * @returns {WidgetType}
   */
  static getBubbleChartType() {
    return new WidgetType({
      can_group_by: true,
      can_drilldown: false,
      requires_group_by: true,
      id: WidgetTypeConstant.TYPE_BUBBLECHART,
      name: i18n.$t('Bubble Chart'),
      /* eslint-disable tap/no-raw-text-js */
      image: 'bubblechart-widget',
      largeImage: 'bubblechart-widget',
      type_groupings: ['datasourced', 'chart', 'series'],
      /* eslint-enable tap/no-raw-text-js */
    });
  }

  /**
   * @returns {WidgetType}
   */
  static getPieChartType() {
    return new WidgetType({
      can_group_by: true,
      can_drilldown: true,
      requires_group_by: false,
      id: WidgetTypeConstant.TYPE_PIECHART,
      name: i18n.$t('Pie Chart'),
      /* eslint-disable tap/no-raw-text-js */
      image: 'piechart-default',
      largeImage: 'piechart-default',
      type_groupings: ['datasourced', 'chart'],
      /* eslint-enable tap/no-raw-text-js */
    });
  }

  /**
   * @returns {WidgetType}
   */
  static getFunnelChartType() {
    return new WidgetType({
      can_group_by: true,
      can_drilldown: true,
      requires_group_by: false,
      id: WidgetTypeConstant.TYPE_FUNNELCHART,
      name: i18n.$t('Funnel Chart'),
      /* eslint-disable tap/no-raw-text-js */
      image: 'funnelchart-default',
      largeImage: 'funnelchart-default',
      type_groupings: ['datasourced', 'chart'],
      /* eslint-enable tap/no-raw-text-js */
    });
  }

  /**
   * @returns {WidgetType}
   */
  static getGaugeChartType() {
    return new WidgetType({
      can_group_by: false,
      can_drilldown: false,
      requires_group_by: false,
      id: WidgetTypeConstant.TYPE_GAUGECHART,
      name: i18n.$t('Gauge Chart'),
      /* eslint-disable tap/no-raw-text-js */
      image: 'gaugechart-default',
      largeImage: 'gaugechart-default',
      type_groupings: ['datasourced', 'chart'],
      /* eslint-enable tap/no-raw-text-js */
    });
  }

  /**
   * @returns {WidgetType}
   */
  static getGeoChartType() {
    return new WidgetType({
      can_group_by: true,
      can_drilldown: true,
      requires_group_by: true,
      id: WidgetTypeConstant.TYPE_GEOCHART,
      name: i18n.$t('Geo Chart'),
      /* eslint-disable tap/no-raw-text-js */
      image: 'geochart-widget',
      largeImage: 'geochart-widget',
      type_groupings: ['datasourced', 'chart'],
      /* eslint-enable tap/no-raw-text-js */
    });
  }

  /**
   * @returns {WidgetType}
   */
  static getMediaType() {
    return new WidgetType({
      can_group_by: false,
      can_drilldown: false,
      requires_group_by: false,
      id: WidgetTypeConstant.TYPE_MEDIA,
      name: i18n.$t('Media'),
      /* eslint-disable tap/no-raw-text-js */
      image: 'media-widget',
      largeImage: 'media-widget',
      type_groupings: ['display'],
      /* eslint-enable tap/no-raw-text-js */
    });
  }

  /**
   * @returns {WidgetType}
   */
  static getAccountManagerType() {
    return new WidgetType({
      can_group_by: false,
      can_drilldown: false,
      requires_group_by: false,
      id: WidgetTypeConstant.TYPE_ACCOUNTMANAGER,
      name: i18n.$t('Admin'),
      /* eslint-disable tap/no-raw-text-js */
      image: 'admin-widget',
      largeImage: 'admin-widget',
      type_groupings: ['datasourced', 'admin'],
      /* eslint-enable tap/no-raw-text-js */
    });
  }

  /**
   * @returns {WidgetType}
   */
  static getExecutiveSummaryType() {
    return new WidgetType({
      can_group_by: false,
      can_drilldown: false,
      requires_group_by: false,
      id: WidgetTypeConstant.TYPE_EXECUTIVESUMMARY,
      name: i18n.$t('Executive Summary'),
      image: 'paragraph-bullets',
      type_groupings: [],
    });
  }

  static getChatGPTType() {
    return new WidgetType({
      can_group_by: false,
      can_drilldown: false,
      requires_group_by: false,
      /* eslint-disable tap/no-raw-text-js */
      id: 'chatgpt',
      name: i18n.$t('ChatGPT'),
      image: 'chatgpt-widget',
      largeImage: 'chatgpt-widget',
      type_groupings: [],
      /* eslint-enable tap/no-raw-text-js */
    });
  }

  /**
   * @returns {WidgetType}
   */
  static getSankeyChartType() {
    return new WidgetType({
      can_group_by: true,
      can_drilldown: false,
      requires_group_by: true,
      id: WidgetTypeConstant.TYPE_SANKEYCHART,
      name: i18n.$t('Sankey Chart'),
      /* eslint-disable tap/no-raw-text-js */
      image: 'sankey-widget',
      largeImage: 'sankey-widget',
      type_groupings: ['datasourced', 'chart'],
      /* eslint-enable tap/no-raw-text-js */
    });
  }

  /**
   * @returns {WidgetType}
   */
  static getPivotTableType() {
    return new WidgetType({
      can_group_by: true,
      can_drilldown: false,
      requires_group_by: true,
      id: WidgetTypeConstant.TYPE_PIVOTTABLE,
      name: i18n.$t('Pivot Table'),
      /* eslint-disable tap/no-raw-text-js */
      image: 'pivottable-widget',
      largeImage: 'pivottable-widget',
      type_groupings: ['datasourced', 'chart'],
      /* eslint-enable tap/no-raw-text-js */
    });
  }
}
